import React, { useEffect, useState } from "react";
import "react-toastify/dist/ReactToastify.css";

import "tippy.js/dist/tippy.css";
import "tippy.js/themes/material.css";

import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import ToyCard from "./toy-card";
import LoadingSpinner from "../common/loading";
import AddToyDialog from "./add-toy-dialog";
import { useWebSocket } from "../ws/websocket";
import { useEventEmitter } from "../ws/event-context";
import { useBackdrop } from "../common/backdrop";
import { Button, Input, Select, FloatButton, Row, Col, Space } from "antd";
import { useRecoilState } from "recoil";
import { userInfoState } from "../global-state";
import { useHistory } from "react-router-dom";
import useUserAuth from "../auth/user-auth";

const PlaygroundView = () => {
  const { openBackdrop, closeBackdrop } = useBackdrop();
  const {
    connectWebSocket,
    disconnectWebSocket,
    wsReady,
    sendAndWaitWs,
    sendAndForgetWs,
  } = useWebSocket();
  const { subscribe } = useEventEmitter();
  const history = useHistory();
  const [playgroundLoading, setPlaygroundLoading] = useState(true);
  const [playgroundToyMap, setPlaygroundToyMap] = useState(new Map());
  const [playgroundToyList, setPlaygroundToyList] = useState([]);
  const [toyFunctions, setToyFunctions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [userAuthState, setUserAuthState] = useState("");
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [scope, setScope] = useState("");
  const {
    isAuthenticated,
    userInfo: authUserInfo,
    checkUserAuthState,
    signOut: userSignOut,
  } = useUserAuth();

  useEffect(() => {
    const initializeAuth = async () => {
      const authResult = await checkUserAuthState();
      setUserAuthState(authResult.isAuthenticated ? "yes" : "no");
      if (authResult.isAuthenticated && authResult.userInfo) {
        setUserInfo(authResult.userInfo);
        connectWebSocket(authResult.userInfo);
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (userAuthState === "yes" && wsReady) {
      setScope(userInfo.activeScope.id);
      getPlayground();
    }
  }, [userAuthState, wsReady]);

  useEffect(() => {
    const { unsubscribe: s1 } = subscribe((target, data) => {
      if (target === "_closed_") {
        // openBackdrop('Connecting...');
      }
    });

    const { unsubscribe: s2 } = subscribe((target, data) => {
      if (target === "/playground/update") {
        const payload = JSON.parse(data.payload);
        playgroundToyMap.set(payload.name, payload);
        const ta = Array.from(playgroundToyMap.entries())
          .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
          .map(([key, value]) => value);
        setPlaygroundToyList(ta);
      }
    });
    return () => {
      s1();
      s2();
    };
  }, []);

  const getPlayground = async () => {
    try {
      setPlaygroundLoading(true);
      const response = await sendAndWaitWs(
        "/playground/read",
        JSON.stringify({}),
      );
      if (response.error) {
        throw new Error(response.error);
      }
      const jsonData = JSON.parse(response.payload);
      playgroundToyMap.clear();
      for (let i = 0; i < jsonData.toys.length; i++) {
        playgroundToyMap.set(jsonData.toys[i].name, jsonData.toys[i]);
      }
      const ta = Array.from(playgroundToyMap.entries())
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .map(([key, value]) => value);
      setPlaygroundToyList(ta);

      setToyFunctions(jsonData.functions || []);
      setPlaygroundLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleLogin = async () => {
    history.push("/user-login");
  };

  const handleLogout = async () => {
    await userSignOut();
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleAddToy = async (name, functions) => {
    try {
      const response = await sendAndWaitWs(
        "/playground/create",
        JSON.stringify({
          name,
          functions,
        }),
      );
      if (response.error) {
        throw new Error(response.error);
      }
      const jsonData = JSON.parse(response.payload);
      getPlayground();
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleDeleteToy = async name => {
    try {
      const response = await sendAndWaitWs(
        "/playground/delete",
        JSON.stringify({
          name,
        }),
      );
      if (response.error) {
        throw new Error(response.error);
      }
      const jsonData = JSON.parse(response.payload);
      getPlayground();
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleUpdateToy = async updatedToy => {
    try {
      await sendAndForgetWs(
        "/playground/update",
        JSON.stringify({
          toy: updatedToy,
        }),
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleToyFunctionEvent = async (name, func) => {
    try {
      const resp = await sendAndForgetWs(
        "/playground/trigger",
        JSON.stringify({
          name,
          function: func,
        }),
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  return (
    <div>
      {userAuthState === "no" ? (
        <div style={{ padding: 32 }}>
          <h6 style={{ marginBottom: 16 }}>
            Playground provides virtual devices for testing usage. The devices
            are bound to a user scope. Please login with your personal account
            and choose the target scope.
          </h6>
          <Button
            type="primary"
            onClick={handleLogin}
            style={{ marginTop: 16 }}
          >
            Login
          </Button>
        </div>
      ) : (
        <>
          {userAuthState === "yes" && (
            <div>
              <Row align="middle" gutter={16}>
                <Col>
                  <UserOutlined style={{ fontSize: 30 }} />
                </Col>
                <Col flex="auto">
                  <Space>
                    <Input
                      value={userInfo.name}
                      disabled
                      size="small"
                      style={{ marginLeft: "20px" }}
                    />
                    <Button
                      type="primary"
                      onClick={handleLogout}
                    >
                      Logout
                    </Button>
                  </Space>
                </Col>
              </Row>

              <Row
                align="middle"
                style={{ marginBottom: "15px", marginTop: "15px" }}
                gutter={16}
              >
                <Col>
                  <div
                    style={{
                      width: "50px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Home
                  </div>
                </Col>
                <Col>
                  <Select
                    value={scope}
                    onChange={value => setScope(value)}
                    placeholder="Select scope"
                    style={{
                      height: "40px",
                      minWidth: 180,
                    }}
                  >
                    {userInfo.scopes.map(userScope => (
                      <Select.Option key={userScope.id} value={userScope.id}>
                        {userScope.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              {playgroundLoading ? (
                <LoadingSpinner />
              ) : (
                <div style={{ flexGrow: 1 }}>
                  <Row gutter={[16, 16]}>
                    {playgroundToyList.map((toy, index) => (
                      <Col xs={12} sm={12} md={8} lg={6} key={index}>
                        <ToyCard
                          toy={toy}
                          onUpdate={handleUpdateToy}
                          onSendEvent={handleToyFunctionEvent}
                          onDelete={handleDeleteToy}
                        />
                      </Col>
                    ))}
                  </Row>
                  <FloatButton
                    type="primary"
                    icon={<PlusOutlined />}
                    style={{ bottom: 30, right: 20 }}
                    onClick={handleOpenDialog}
                    tooltip="Add Toy"
                  />
                  <AddToyDialog
                    open={openDialog}
                    features={toyFunctions}
                    onClose={handleCloseDialog}
                    onSave={handleAddToy}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PlaygroundView;
